import api from '../core';
import type { 
  Workspace, 
  WorkspaceMember, 
  WorkspaceAccessLevel 
} from '../../types';
import type { WorkspaceHistoryEntry } from '../../pages/WorkspaceManagement/types';
import { formatDate } from '../../utils/date';

const formatWorkspaceDates = (workspace: Workspace): Workspace => ({
  ...workspace,
  created_at: formatDate(workspace.created_at),
  updated_at: formatDate(workspace.updated_at),
  members: workspace.members.map(member => ({
    ...member,
    created_at: formatDate(member.created_at),
    updated_at: formatDate(member.updated_at)
  }))
});

/**
 * API functions for workspace actions
 * @namespace workspacesApi
 */
export const workspacesApi = {
  /**
   * Gets all workspaces
   * @function getAll
   * @returns {Promise<Workspace[]>}
   */
  getAll: async (): Promise<Workspace[]> => {
    const response = await api.get('/workspaces/list');
    return response.data.map(formatWorkspaceDates);
  },

  /**
   * Gets a workspace by ID
   * @function get
   * @param {number} id - Workspace ID
   * @returns {Promise<Workspace>}
   */
  get: async (id: number): Promise<Workspace> => {
    const response = await api.get(`/workspaces/details/${id}`);
    return formatWorkspaceDates(response.data);
  },

  /**
   * Creates a workspace
   * @function create
   * @param {{ name: string; description?: string }} data - Workspace data
   * @returns {Promise<Workspace>}
   */
  create: async (data: { 
    name: string; 
    description?: string;
    location?: {
      latitude: number;
      longitude: number;
      source: string;
      timestamp: string;
      description?: string;
    };
  }): Promise<Workspace> => {
    const response = await api.post('/workspaces/create', data);
    return formatWorkspaceDates(response.data);
  },

  /**
   * Updates a workspace
   * @function update
   * @param {number} id - Workspace ID
   * @param {{ name?: string; description?: string }} data - Workspace data to update
   * @returns {Promise<Workspace>}
   */
  update: async (id: number, data: { 
    name?: string; 
    description?: string;
    custom_fields?: Record<string, string>;
    location?: {
      latitude: number;
      longitude: number;
      source: string;
      timestamp: string;
      description?: string;
    };
  }): Promise<Workspace> => {
    const response = await api.put(`/workspaces/update/${id}`, data);
    return formatWorkspaceDates(response.data);
  },

  /**
   * Deletes a workspace
   * @function delete
   * @param {number} id - Workspace ID
   * @param {boolean} [force=false] - Force delete
   * @returns {Promise<void>}
   */
  delete: async (id: number, force: boolean = false): Promise<void> => {
    await api.delete(`/workspaces/delete/${id}`, {
      params: { force }
    });
  },

  /**
   * Adds a member to a workspace
   * @function addMember
   * @param {number} workspaceId - Workspace ID
   * @param {{ email: string; access_level: WorkspaceAccessLevel }} data - Member data
   * @returns {Promise<WorkspaceMember>}
   */
  addMember: async (workspaceId: number, data: { 
    email: string; 
    access_level: WorkspaceAccessLevel 
  }): Promise<WorkspaceMember> => {
    const response = await api.post(`/workspaces/members/${workspaceId}/add`, data);
    return response.data;
  },

  /**
   * Updates a member in a workspace
   * @function updateMember
   * @param {number} workspaceId - Workspace ID
   * @param {number} userId - User ID
   * @param {{ access_level: WorkspaceAccessLevel }} data - Member access level to update
   * @returns {Promise<WorkspaceMember>}
   */
  updateMember: async (workspaceId: number, userId: number, data: { 
    access_level: WorkspaceAccessLevel 
  }): Promise<WorkspaceMember> => {
    const response = await api.put(`/workspaces/members/${workspaceId}/update/${userId}`, data);
    return response.data;
  },

  /**
   * Removes a member from a workspace
   * @function removeMember
   * @param {number} workspaceId - Workspace ID
   * @param {number} userId - User ID
   * @returns {Promise<void>}
   */
  removeMember: async (workspaceId: number, userId: number): Promise<void> => {
    await api.delete(`/workspaces/members/${workspaceId}/remove/${userId}`);
  },

  /**
   * Transfers ownership of a workspace
   * @function transferOwnership
   * @param {number} workspaceId - Workspace ID
   * @param {number} userId - User ID to transfer ownership to
   * @returns {Promise<Workspace>}
   */
  transferOwnership: async (workspaceId: number, userId: number): Promise<Workspace> => {
    const response = await api.post(`/workspaces/members/${workspaceId}/transfer/${userId}`);
    return response.data;
  },

  /**
   * Gets the history of a workspace
   * @function getHistory
   * @param {number} workspaceId - Workspace ID
   * @returns {Promise<WorkspaceHistoryEntry[]>}
   */
  getHistory: async (workspaceId: number): Promise<WorkspaceHistoryEntry[]> => {
    const response = await api.get(`/workspaces/${workspaceId}/history`);
    const entries = Array.isArray(response.data) ? response.data : [];
    return entries.map((entry: WorkspaceHistoryEntry) => ({
      ...entry,
      timestamp: formatDate(entry.timestamp)
    }));
  }
};
